import createClient, { type Client } from "openapi-fetch";

import { getCSRFToken } from "./_api";
import { logout } from "utils/accounts";

export interface GeneratedSchedugramServerClientConfig {
  baseUrl?: string;
  fetchImpl?: typeof fetch;
  getCsrfToken?: (force?: boolean) => Promise<string | undefined>;
  maxCsrfRetries?: number;
}

export interface OpenApiResponse<TData, TError = unknown> {
  data?: TData;
  error?: TError;
  response: Response;
}

export class GeneratedSchedugramServerApiError extends Error {
  status: number;
  body: string;
  response: {
    status: number;
    data: unknown;
  };

  constructor(status: number, body: string) {
    const bodySnippet = body.trim().slice(0, 300);
    super(
      bodySnippet
        ? `Generated Schedugram Server request failed with status ${status}: ${bodySnippet}`
        : `Generated Schedugram Server request failed with status ${status}`
    );
    this.name = "GeneratedSchedugramServerApiError";
    this.status = status;
    this.body = body;
    this.response = {
      status,
      data: parseOpenApiErrorBody(body),
    };
  }
}

export class GeneratedSchedugramServerClient<Paths extends object> {
  readonly client: Client<Paths>;

  constructor({
    baseUrl = "",
    fetchImpl = getDefaultFetch(),
    getCsrfToken = getCSRFToken,
    maxCsrfRetries = 3,
  }: GeneratedSchedugramServerClientConfig = {}) {
    this.client = createClient<Paths>({
      baseUrl: getBaseUrl(baseUrl),
      fetch: (request) =>
        fetchWithSchedugramServerAuth({
          request,
          fetchImpl,
          getCsrfToken,
          maxCsrfRetries,
        }),
    });

    // Legacy controllers reached through the tsoa bridge may end a successful
    // request with `res.sendStatus(200)`, sending a non-JSON body ("OK",
    // text/plain) that openapi-fetch throws on while JSON-parsing. Normalise
    // non-JSON 2xx bodies so the parse never throws. Scoped to callers expecting
    // JSON (the default) — `parseAs: "text"` etc. is left untouched. (SKE-4075)
    this.client.use({
      onResponse: ({ response, options }) =>
        normalizeNonJsonSuccess(response, options.parseAs),
    });
  }

  async unwrap<TData, TError = unknown>(
    result: OpenApiResponse<TData, TError>
  ): Promise<TData> {
    if (!result.response.ok) {
      throw new GeneratedSchedugramServerApiError(
        result.response.status,
        stringifyOpenApiError(result.error)
      );
    }

    if (result.data === undefined || result.data === null) {
      throw new GeneratedSchedugramServerApiError(
        result.response.status,
        "Empty response body"
      );
    }

    return result.data;
  }

  async ensureOk<TData, TError = unknown>(
    result: OpenApiResponse<TData, TError>
  ): Promise<void> {
    if (!result.response.ok) {
      throw new GeneratedSchedugramServerApiError(
        result.response.status,
        stringifyOpenApiError(result.error)
      );
    }
  }
}

function getBaseUrl(apiBaseUrl: string | undefined): string {
  return apiBaseUrl?.replace(/\/+$/, "") ?? "";
}

function getDefaultFetch(): typeof fetch {
  if (globalThis.fetch) {
    return globalThis.fetch.bind(globalThis);
  }

  return missingFetch;
}

const missingFetch: typeof fetch = async () => {
  throw new GeneratedSchedugramServerApiError(
    0,
    "global fetch is not available"
  );
};

interface FetchWithSchedugramServerAuthOptions {
  request: Request;
  fetchImpl: typeof fetch;
  getCsrfToken: (force?: boolean) => Promise<string | undefined>;
  maxCsrfRetries: number;
}

async function fetchWithSchedugramServerAuth({
  request,
  fetchImpl,
  getCsrfToken,
  maxCsrfRetries,
}: FetchWithSchedugramServerAuthOptions): Promise<Response> {
  let attempt = 0;
  let requestTemplate = request;
  let forceCsrf = false;

  while (true) {
    const authenticatedRequest = await withSchedugramServerAuth(
      requestTemplate,
      getCsrfToken,
      forceCsrf
    );
    requestTemplate = authenticatedRequest.clone();

    const response = await fetchImpl(authenticatedRequest);

    if (response.status === 401) {
      logout();
      return response;
    }

    if (
      response.status !== 403 ||
      attempt >= maxCsrfRetries ||
      !(await hasCsrfReset(response))
    ) {
      return response;
    }

    attempt += 1;
    forceCsrf = true;
  }
}

async function withSchedugramServerAuth(
  request: Request,
  getCsrfToken: (force?: boolean) => Promise<string | undefined>,
  forceCsrf: boolean
): Promise<Request> {
  const headers = new Headers(request.headers);
  headers.set("Accept", "application/json");

  if (requiresCsrfToken(request.method)) {
    const csrfToken = await getCsrfToken(forceCsrf);

    if (csrfToken) {
      headers.set("X-XSRF-TOKEN", csrfToken);
    }
  }

  return new Request(request, {
    credentials: "same-origin",
    headers,
  });
}

function requiresCsrfToken(method: string): boolean {
  return !["GET", "HEAD", "OPTIONS"].includes(method.toUpperCase());
}

async function hasCsrfReset(response: Response): Promise<boolean> {
  try {
    const body = await response.clone().json();
    return body?.csrfReset === 1;
  } catch {
    return false;
  }
}

// Statuses that must not carry a body — leave these untouched (constructing a
// Response with a body for them throws, and openapi-fetch already treats them
// as empty).
const NULL_BODY_STATUSES = new Set([204, 205, 304]);

// Matches application/json and structured-suffix JSON types such as
// application/ld+json and application/problem+json.
function isJsonContentType(contentType: string | null): boolean {
  return /application\/([\w.+-]+\+)?json/i.test(contentType ?? "");
}

function normalizeNonJsonSuccess(
  response: Response,
  parseAs: string | undefined
): Response | undefined {
  // Only touch responses the caller expects as JSON. openapi-fetch defaults
  // parseAs to "json" before calling middleware, so treat both "json" and an
  // unset value as JSON; a caller that opted into text/blob/stream (e.g.
  // parseAs: "text") wants the raw body left untouched.
  if (parseAs !== undefined && parseAs !== "json") {
    return undefined;
  }
  if (!response.ok || NULL_BODY_STATUSES.has(response.status)) {
    return undefined;
  }
  if (isJsonContentType(response.headers.get("content-type"))) {
    return undefined;
  }

  // Replace the non-JSON success body (e.g. "OK" from res.sendStatus) with "{}"
  // so openapi-fetch parses it instead of throwing. "{}" (not "null") keeps
  // `unwrap` usable by any future consumer. Original headers are preserved;
  // only content-type is overridden. Mutations that use `ensureOk` ignore the
  // body, and JSON responses keep their real body via the guard above.
  const headers = new Headers(response.headers);
  headers.set("content-type", "application/json");
  return new Response("{}", {
    status: response.status,
    statusText: response.statusText,
    headers,
  });
}

function stringifyOpenApiError(error: unknown): string {
  if (error === undefined || error === null) {
    return "{}";
  }

  if (typeof error === "string") {
    return error;
  }

  return JSON.stringify(error);
}

function parseOpenApiErrorBody(body: string): unknown {
  try {
    return JSON.parse(body);
  } catch {
    return {
      message: body,
    };
  }
}
